import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, Empty } from "@/components/layout/AppShell";
import { PostCard, ReaderCard, ServiceCard } from "@/components/Cards";
import { ThreadRow } from "@/pages/Community";

export default function Home() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "for_you";
  const specialty = sp.get("specialty") || "";
  const [posts, setPosts] = useState<any[]>([]);
  const [readers, setReaders] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [specs, setSpecs] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const ctype = sp.get("type") || "";
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/readers/specialties").then((r) => setSpecs(r.data));
    api.get("/readers/creator-types").then((r) => setTypes(r.data));
    api.get("/services", { params: { limit: 6 } }).then((r) => setServices(r.data));
  }, []);
  useEffect(() => { api.get("/readers", { params: { limit: 6, type: ctype || undefined } }).then((r) => setReaders(r.data)); }, [ctype]);
  useEffect(() => {
    setLoading(true);
    api.get("/posts/feed", { params: { tab, specialty: specialty || undefined } }).then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, [tab, specialty]);

  const setParam = (k: string, v: string) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n); };
  const likeThread = async (t: any) => { if (!user) return; const { data } = await api.post(`/community/threads/${t.id}/like`); setPosts((l) => l.map((x) => x.id === t.id ? { ...x, liked: data.liked, likes_count: x.likes_count + (data.liked ? 1 : -1) } : x)); };

  return (
    <Page>
      {!user && (
        <section className="mb-12 grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-8 items-end" data-testid="hero">
          <div>
            <p className="eyebrow mb-4">Readers, creators & community · Tarot, oracle, Lenormand, astrology & more</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[0.95]">Readers you can trust.<br /><em className="font-medium text-[#581C25]">Readings you keep.</em></h1>
            <p className="mt-5 text-[#5E5C56] max-w-lg">Book written, voice or live-chat readings from independent readers. Every reading lives in your private archive, with the cards pulled at The Table.</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/register" className="btn-wine" data-testid="hero-cta-register">Start as a member <ArrowRight size={16} /></Link>
              <Link to="/onboarding" className="btn-ghost" data-testid="hero-cta-reader">Open a reader channel</Link>
            </div>
          </div>
          <div className="hidden md:grid grid-cols-2 gap-3">
            {readers.slice(0, 2).map((r) => <Link key={r.id} to={`/@${r.username}`} className="aspect-[3/4] rounded-md overflow-hidden bg-[#1A1917] relative"><img src={r.avatar} alt={r.name} className="h-full w-full object-cover grayscale hover:grayscale-0 transition-[filter] duration-700" /><span className="absolute bottom-3 left-3 serif text-[#F6F4EE] text-xl drop-shadow">{r.name}</span></Link>)}
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-10">
        <div className="min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4 min-w-0">
            <div className="flex gap-2 shrink-0">
            {[["for_you", "For you"], ["following", "Following"]].map(([k, l]) => (
              <button key={k} onClick={() => setParam("tab", k)} className={`chip ${tab === k ? "chip-on" : ""}`} data-testid={`feed-tab-${k}`}>{l}</button>
            ))}
            </div>
            <div className="sm:ml-auto flex gap-2 overflow-x-auto scrollbar-none w-full sm:max-w-[60%] min-w-0">
              {specs.map((s) => <button key={s} onClick={() => setParam("specialty", specialty === s ? "" : s)} className={`chip whitespace-nowrap ${specialty === s ? "chip-on" : ""}`} data-testid={`specialty-chip-${s.replace(/\s/g, "-")}`}>{s}</button>)}
            </div>
          </div>
          {loading ? <div className="space-y-4">{[0, 1, 2].map((i) => <div key={i} className="card-tactile h-40 animate-pulse" />)}</div> :
            posts.length === 0 ? <Empty title={tab === "following" ? "Your following feed is quiet." : "Nothing here yet."} hint={tab === "following" ? "Follow readers to see their posts here." : "Try another specialty."} /> :
            <div className="space-y-4" data-testid="feed">{posts.map((p) => p.feed_kind === "thread" ? <ThreadRow key={p.id} t={p} compact onLike={likeThread} /> : <PostCard key={p.id} post={p} />)}</div>}
        </div>
        <aside className="space-y-8">
          <section>
            <div className="flex items-baseline justify-between mb-3"><h2 className="text-lg font-semibold">{ctype ? `${ctype}s to follow` : "Readers to follow"}</h2><Link to="/search" className="mono text-[10px] uppercase tracking-wider text-[#581C25]">All</Link></div>
            <div className="flex gap-1.5 overflow-x-auto scrollbar-none mb-3" data-testid="type-chips">{types.map((t) => <button key={t} onClick={() => setParam("type", ctype === t ? "" : t)} className={`chip !py-0.5 !text-[10px] whitespace-nowrap ${ctype === t ? "chip-on" : ""}`} data-testid={`type-chip-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</button>)}</div>
            <div className="space-y-3">{readers.slice(0, 3).map((r) => <ReaderCard key={r.id} r={r} />)}{readers.length === 0 && <p className="text-sm text-[#5E5C56]">No {ctype.toLowerCase()}s yet.</p>}</div>
          </section>
          <section>
            <div className="flex items-baseline justify-between mb-3"><h2 className="text-lg font-semibold">Readings this week</h2><Link to="/search?kind=services" className="mono text-[10px] uppercase tracking-wider text-[#581C25]">Browse</Link></div>
            <div className="space-y-3">{services.slice(0, 3).map((s) => <ServiceCard key={s.id} s={s} />)}</div>
          </section>
        </aside>
      </div>
    </Page>
  );
}

export function Live() {
  const { features } = useAuth();
  return (
    <Page className="max-w-2xl text-center py-24" >
      <p className="eyebrow mb-4">Live</p>
      <h1 className="text-4xl sm:text-5xl font-semibold" data-testid="live-coming-soon">{features.live ? "Coming soon." : "Live is paused."}</h1>
    </Page>
  );
}

export function Search() {
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") || "";
  const kind = sp.get("kind") || "readers";
  const type = sp.get("type") || "";
  const [specs, setSpecs] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const specialty = sp.get("specialty") || "";
  const [res, setRes] = useState<any[]>([]);
  useEffect(() => { api.get("/readers/specialties").then((r) => setSpecs(r.data)); api.get("/readers/creator-types").then((r) => setTypes(r.data)); }, []);
  useEffect(() => {
    const params = { q: q || undefined, specialty: specialty || undefined, type: type || undefined };
    const url = kind === "readers" ? "/readers" : kind === "services" ? "/services" : "/posts/feed";
    api.get(url, { params }).then((r) => setRes(r.data));
  }, [q, kind, specialty, type]);
  const set = (k: string, v: string) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n); };
  return (
    <Page>
      <h1 className="text-4xl font-semibold mb-6">Search</h1>
      <input className="input-ed text-base mb-4" placeholder="Search readers, readings, posts…" value={q} onChange={(e) => set("q", e.target.value)} data-testid="search-input" />
      <div className="flex flex-wrap gap-2 mb-3">
        {["readers", "services", "posts"].map((k) => <button key={k} onClick={() => set("kind", k)} className={`chip ${kind === k ? "chip-on" : ""}`} data-testid={`search-kind-${k}`}>{k}</button>)}
      </div>
      <div className="flex gap-2 overflow-x-auto scrollbar-none mb-3">{specs.map((s) => <button key={s} onClick={() => set("specialty", specialty === s ? "" : s)} className={`chip whitespace-nowrap ${specialty === s ? "chip-on" : ""}`}>{s}</button>)}</div>
      {kind === "readers" && <div className="flex gap-2 overflow-x-auto scrollbar-none mb-6" data-testid="search-type-chips">{types.map((t) => <button key={t} onClick={() => set("type", type === t ? "" : t)} className={`chip whitespace-nowrap !text-[11px] ${type === t ? "chip-on" : ""}`} data-testid={`search-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</button>)}</div>}
      {res.length === 0 ? <Empty title="No results." /> :
        <div className={`grid gap-4 ${kind === "posts" ? "max-w-2xl" : "sm:grid-cols-2 lg:grid-cols-3"}`} data-testid="search-results">
          {res.map((x) => kind === "readers" ? <ReaderCard key={x.id} r={x} /> : kind === "services" ? <ServiceCard key={x.id} s={x} /> : <PostCard key={x.id} post={x} />)}
        </div>}
    </Page>
  );
}
